<template>
    <div class="stack-wrapper">
        <div class="stack-container" :class="{'is-empty': !hasContent, 'has-content': hasContent}">
            <!-- Default content -->
            <div :class="{ 'stack-fluid': fluid }" class="stack-column">
                <div class="column-contents">
                    <slot />
                </div>
            </div>

            <!-- Additional panels -->
            <div v-for="(panel, index) in panels" :key="panel.uuid"
                 :style="style(panel.config)" :id="getPanelElementId(panel)"
                 :class="{ 'stack-fluid': fluid }"
                 class="stack-column admin-content">

                <!-- Panel header -->
                <div class="stack-header-container">
                    <div class="stack-header">
                        <table class="table no-margin" :class="$style.stackHeader">
                            <tr>
                                <td>
                                    <stack-view :item="panel" :index="index" header />
                                </td>

                                <td v-if="hasOptions(panel)" width="32">
                                    <div :class="{[$style.headerButton]: true, [$style.active]: panel.showOptions}"
                                         v-tooltip.left="'Weitere Aktionen'"
                                         @click="$store.commit('OPTIONS', index)">
                                        <i class="mdi mdi-more-vert"></i>
                                    </div>
                                </td>

                                <td v-if="!panel.loading" width="32">
                                    <div :class="$style.headerButton" @click="refreshPanel(panel, index)"
                                         v-tooltip.left="'Daten aktualisieren'">
                                        <i class="mdi mdi-refresh"></i>
                                    </div>
                                </td>

                                <td v-if="panel.config.closable" width="32">
                                    <div :class="$style.headerButton" @click="$store.commit('CLOSE', index)">
                                        <i class="mdi mdi-close"></i>
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>

                <div class="column-container">
                    <div class="column-contents">
                        <stack-view v-show="!panel.showOptions || !hasOptions(panel)"
                                    :item="panel" :index="index" />
                        <stack-view v-if="panel.showOptions && hasOptions(panel)"
                                    :item="panel" :index="index" options />
                    </div>
                </div>
            </div>

            <div v-if="empty(panels) && (hasContent || $slots.empty !== undefined)"
                 class="stack-column admin-content"
                 :class="emptyClass">
                <div class="column-contents">
                    <slot name="empty" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import StackView from './components/view';
import store from './store';

import { getPanelElementId } from './utils/helpers';
import { getSharedData } from './utils/support';
import { eventHub } from './utils/reactive';
import actions from './utils/actions';
import { assert } from '../utils/assert';
import { CLOSE, FILL, REFRESH, SUBSCRIBE } from './store/mutation-types';

export default {
  store,

  props: {
    isEmpty: {
      type: Boolean,
      default: true,
    },

    emptyClass: {
      type: [String, Array, Object],
    },

    fluid: {
      type: Boolean,
      default: false,
    },
  },

  components: { StackView },

  computed: {
    ...mapGetters([
      'panels',
    ]),

    hasContent() {
      return !this.isEmpty || this.panels.length > 0;
    },
  },

  beforeCreate() {
    // Make sure even the children can access this instance
    this.$root.$stack = this;
  },

  beforeDestroy() {
    eventHub.$off('update', this.fillData);
  },

  // Add asynchronously loaded, shared data accessible by 'shared'
  data() {
    const shared = getSharedData();
    const data = {};

    for (const key in shared) {
      const config = shared[key];
      data[key] = config.default;
      data[`${key}Error`] = undefined;
      data[`${key}Loaded`] = false;
      data[`${key}Request`] = undefined;
    }

    return {
      shared: data,
    };
  },

  // Update our stack whenever something changes
  created() {
    eventHub.$on('update', this.fillData);

    // Whenever we do a refresh, reemit the subscribe event
    this.$store.subscribe(({ type }) => {
      if (type === REFRESH) {
        this.$emit('subscribe', this);
      }
    });

    this.refresh();
    window.addEventListener('hashchange', this.refresh);

    this.$emit('loaded', this);
  },

  methods: {
    getPanelElementId,

    hasOptions(panel) {
      return !panel.loading && this.empty(panel.errors) && panel.config.options.length > 0;
    },

    refresh() {
      this.$store.dispatch('refresh');

      // Automatically close models that should be deleted
      this.subscribe(actions.modelDeleted, '*', (_, id, index) => this.$store.commit(CLOSE, index));

      // Notify every other listener in the stack
      eventHub.$emit('refresh');
    },

    refreshPanel({ id, key }, index) {
      // Refresh contents
      this.$store.dispatch('display', {
        key, id, strict: true, index,
      });
    },

    style(config) {
      return {
        minWidth: config.minWidth && `${config.minWidth}px`,
      };
    },

    subscribe(action, type, func) {
      this.$store.commit(SUBSCRIBE, { action, type, func });
    },

    loadShared(key, refresh = false) {
      const config = getSharedData()[key];
      assert(config, `No shared data config for '${key}' available`);

      const $shared = this.shared;
      const loaded = `${key}Loaded`;
      const request = `${key}Request`;

      if (!refresh && ($shared[loaded] || $shared[request])) {
        return $shared[request];
      }

      return $shared[request] = new Promise(config.promise)
        .then((data) => {
          $shared[key] = data;
          $shared[loaded] = true;
          $shared[request] = null;
        })
        .catch((error) => {
          $shared[`${key}Error`] = error;
          $shared[loaded] = false;
          $shared[request] = null;
        });
    },

    /**
     * Use this in things like typeaheads when referencing
     * chunks of data that might take a while to load.
     *
     * @param key
     */
    fetchShared(key) {
      return (query, process) => {
        const promise = this.loadShared(key);

        if (promise) {
          promise.then(() => process(this.shared[key]));
          return;
        }

        process(this.shared[key]);
      };
    },

    fillData(data) {
      this.$store.commit(FILL, data);
    },
  },
};
</script>

<style lang="scss" module>
    @import "../../sass/variables";

    // Button styling for the headers
    .headerButton {
        cursor: pointer;

        padding: 5px 6px;
        line-height: 1;
        font-size: 18px;
        border-radius: 3px;

        &:hover, &.active {
            background-color: $kw-blue-lighter;
            color: #f6f6f6 !important;
        }

        &.active {
            background-color: $kw-blue-dark;
        }
    }

    .stackHeader {
        table-layout: fixed;
        height: 46px;

        > tr > td {
            border-left: none;
            padding-left: 2px;

            &:first-child {
                padding-left: 0;
            }
        }
    }
</style>

<style lang="scss">
    @import "~bootstrap-sass/assets/stylesheets/bootstrap/variables";

    .stack-wrapper {
        height: 100%;
        position: relative;
    }

    .stack-container {
        height: 100%;
        position: relative;
        overflow-x: auto;

        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        align-content: stretch;
        align-items: stretch;
    }

    .stack-column {
        display: inline-flex;
        flex-direction: column;
        position: relative;
        z-index: 2;
        flex: 1 0;
    }

    .stack-header-container {
        z-index: 1;
        flex: 0 0 auto;
    }

    .stack-header {
        background-color: #f6f6f6;
        box-shadow: 0 2px 4px rgba(black, .125);
        position: relative;
        padding: 12px 15px;

        // Fixes box-shadow not appearing
        z-index: 9;

        h1, h2, h3, h4, h5, h6 {
            margin: 0;

            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            letter-spacing: -0.75px;
        }

        h5 {
            color: rgba(black, .6);
            margin-bottom: 2px;
            letter-spacing: -0.2px;

            strong, b { font-weight: 700 }
            &:last-child {  margin-bottom: 0;  }
        }
    }

    .column-container {
        flex: 1 1 auto;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        position: relative;
    }

    .column-contents {
        overflow-y: auto;
        height: 100%;
        -webkit-overflow-scrolling: touch;
    }

    .stack-options > .media {
        background-color: #f3f3f3;
        margin-top: 0;

        > .media-left {
            box-shadow: inset -5px 0 5px -5px rgba(black, 0.3);
            min-width: 70px;
            text-align: center;
            padding-top: 9px;
            padding-left: 10px;
            color: #666;

            > i {
                font-size: 2em;
            }
        }

        > .media-body {
            background-color: white;
            border-top: 2px solid #eee;
        }

        &:first-child > .media-body {
            border-top-color: transparent;
        }

        &:last-child {
            box-shadow: 0 1px 2px rgba(black, 0.2);
        }
    }

    .stack-panel {
        background: #eee;
        padding: 15px;
    }

    .stack-options {
        padding: 0;
        position: absolute;
        top: 0;
        bottom: 0;
        overflow: auto;
        -webkit-overflow-scrolling: touch;
    }

    @media (max-width: $screen-sm-max) {
        .stack-header {
            padding-top: 5px;
            padding-bottom: 5px;

            h3 {
                font-size: 18px;
            }

            h5 {
                font-size: 12px;
            }
        }

        .stack-container {
            > .stack-column {
                display: none;
            }

            &.is-empty > .stack-column:first-child,
            &.has-content > .stack-column:last-child {
                display: inline-flex;

                &:not(.stack-fluid) {
                    min-width: 100% !important;
                }
            }

            .stack-options > .media > .media-left {
                min-width: auto;
                padding-top: 13px;

                > i {
                    font-size: 20px;
                }
            }
        }
    }

    // Make the placeholders more visible
    .vue-content-placeholders-heading__subtitle,
    .vue-content-placeholders-text__line,
    .vue-content-placeholders-img {
        background-color: #e8e8e8;
    }

    // Animation
    .fade-enter-active, .fade-leave-active {
        transition: opacity 100ms;
    }

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }
</style>



// WEBPACK FOOTER //
// resources/assets/js/view-stack/App.vue